import { useCallback, useEffect, useMemo, useState } from "react";
import { deleteRow, insertRow, listRows, updateRow } from "@/lib/adminApi";
import {
  NOTIFICATION_AUDIENCES,
  NOTIFICATION_STATUS,
  NOTIFICATION_TYPE,
  metaFor,
  optionsFrom,
  type AdminNotification,
} from "@/lib/adminContent";
import {
  DataTable,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  TextField,
  btnGhost,
  btnPrimary,
  fieldInput,
  textInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatDate } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

const EMPTY_NOTIFICATION = {
  title: "",
  message: "",
  type: "info",
  audience: "all",
  status: "sent",
};

export default function AdminNotifications() {
  const [rows, setRows] = useState<AdminNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_NOTIFICATION });
  const [saving, setSaving] = useState(false);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<AdminNotification>("admin_notifications", "created_at"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load notifications.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id: number, patch: Partial<AdminNotification>, message?: string) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("admin_notifications", id, patch);
      if (message) notify(message);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const createNotification = async () => {
    if (!draft.title.trim()) {
      setError("A notification title is required.");
      return;
    }
    setSaving(true);
    try {
      const created = await insertRow<AdminNotification>("admin_notifications", { ...draft, title: draft.title.trim() });
      setRows((list) => [created, ...list]);
      setCreateOpen(false);
      setDraft({ ...EMPTY_NOTIFICATION });
      notify("Notification sent.");
    } catch {
      setError("We couldn't create that notification. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    try {
      await deleteRow("admin_notifications", id);
      setRows((list) => list.filter((row) => row.id !== id));
      setSelectedId(null);
      notify("Notification deleted.");
    } catch {
      setError("We couldn't delete that notification. Please try again.");
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (typeFilter !== "all" && row.type !== typeFilter) return false;
      if (!query) return true;
      return [row.title, row.message || "", row.audience].join(" ").toLowerCase().includes(query);
    });
  }, [rows, search, typeFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;

  const columns: Column<AdminNotification>[] = [
    {
      key: "title",
      header: "Notification",
      primary: true,
      render: (row) => (
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-full bg-accent-100 text-accent-900 shrink-0 flex items-center justify-center">
            <i className="ri-notification-3-line"></i>
          </span>
          <div className="min-w-0 max-w-md">
            <p className="text-sm font-semibold text-foreground-950">{row.title}</p>
            <p className="text-xs text-foreground-500 mt-0.5 line-clamp-1">{row.message || "—"}</p>
          </div>
        </div>
      ),
    },
    { key: "type", header: "Type", render: (row) => <StatusPill {...metaFor(NOTIFICATION_TYPE, row.type)} withDot={false} /> },
    { key: "audience", header: "Audience", render: (row) => <span className="text-sm text-foreground-700 capitalize">{row.audience}</span> },
    { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(NOTIFICATION_STATUS, row.status)} /> },
    { key: "created", header: "Created", render: (row) => <span className="text-sm text-foreground-600 whitespace-nowrap">{formatDate(row.created_at)}</span> },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Marketing"
        title="Notifications"
        subtitle="Announcements and alerts pushed to visitors, farmers and the admin team — seasonal openings, advisories and system notes."
        actions={
          <button type="button" onClick={() => setCreateOpen(true)} className={btnPrimary}>
            <i className="ri-add-line"></i> New Notification
          </button>
        }
      />

      <StatCards
        stats={[
          { label: "Total", value: rows.length, icon: "ri-notification-3-line", tone: "primary" },
          { label: "Sent", value: rows.filter((row) => row.status === "sent").length, icon: "ri-send-plane-line", tone: "primary" },
          { label: "Promotions", value: rows.filter((row) => row.type === "promo").length, icon: "ri-megaphone-line", tone: "accent" },
          { label: "Alerts & system", value: rows.filter((row) => row.type === "alert" || row.type === "system").length, icon: "ri-alert-line", tone: "secondary" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search notifications…" />
          <Select label="Type" value={typeFilter} onChange={setTypeFilter} options={[{ value: "all", label: "All types" }, ...optionsFrom(NOTIFICATION_TYPE)]} />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">{filtered.length} notifications</span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading notifications…" />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-notification-3-line" title={rows.length === 0 ? "No notifications yet" : "No notifications match"} message="Send an announcement to keep everyone informed." />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.title || "Notification"}
        subtitle="Notification detail"
        footer={
          selected ? (
            <button type="button" onClick={() => remove(selected.id)} className={btnGhost}>
              <i className="ri-delete-bin-line"></i> Delete
            </button>
          ) : null
        }
      >
        {selected ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill {...metaFor(NOTIFICATION_TYPE, selected.type)} withDot={false} />
              <StatusPill {...metaFor(NOTIFICATION_STATUS, selected.status)} />
            </div>

            <Field label="Title">
              <input value={selected.title} onChange={(e) => update(selected.id, { title: e.target.value })} className={textInput} />
            </Field>
            <Field label="Message">
              <textarea value={selected.message || ""} onChange={(e) => update(selected.id, { message: e.target.value })} rows={4} maxLength={500} className={`${textInput} resize-none`}></textarea>
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Type">
                <select value={selected.type} onChange={(e) => update(selected.id, { type: e.target.value }, "Type updated.")} className={fieldInput}>
                  {optionsFrom(NOTIFICATION_TYPE).map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}
                </select>
              </Field>
              <Field label="Audience">
                <select value={selected.audience} onChange={(e) => update(selected.id, { audience: e.target.value })} className={fieldInput}>
                  {NOTIFICATION_AUDIENCES.map((audience) => (<option key={audience} value={audience}>{audience}</option>))}
                </select>
              </Field>
            </div>

            <Field label="Status">
              <select value={selected.status} onChange={(e) => update(selected.id, { status: e.target.value }, "Status updated.")} className={fieldInput}>
                {optionsFrom(NOTIFICATION_STATUS).map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}
              </select>
            </Field>
          </>
        ) : null}
      </Drawer>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Send a notification"
        subtitle="New announcement"
        footer={
          <>
            <button type="button" onClick={() => setCreateOpen(false)} className={btnGhost}>Cancel</button>
            <button type="button" onClick={createNotification} disabled={saving} className={btnPrimary}>
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>}
              Send
            </button>
          </>
        }
      >
        <TextField label="Title">
          <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="e.g. Paddy harvest walks now open" className={textInput} />
        </TextField>
        <TextField label="Message">
          <textarea value={draft.message} onChange={(e) => setDraft({ ...draft, message: e.target.value })} rows={3} maxLength={500} className={`${textInput} resize-none`}></textarea>
        </TextField>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label="Type">
            <select value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })} className={fieldInput}>
              {optionsFrom(NOTIFICATION_TYPE).map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}
            </select>
          </TextField>
          <TextField label="Audience">
            <select value={draft.audience} onChange={(e) => setDraft({ ...draft, audience: e.target.value })} className={fieldInput}>
              {NOTIFICATION_AUDIENCES.map((audience) => (<option key={audience} value={audience}>{audience}</option>))}
            </select>
          </TextField>
        </div>
      </Modal>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Changes save automatically.
      </p>
      {node}
    </div>
  );
}